export interface ThemeConfig {
    id: string;
    name: string;
    description: string;
    colors: {
        background: [number, number]; // Gradient colors
        reelFrame: number;
        reelBorder: number;
        paylineColors: number[];
        uiAccent1: number; // Primary UI color (cyan in 80s theme)
        uiAccent2: number; // Secondary UI color (magenta in 80s theme)
        uiAccent3: number; // Tertiary UI color (yellow in 80s theme)
        winColor: number;
        textColor: number;
    };
    symbols: {
        path: string; // Path to symbol images folder
        style: 'neon' | 'realistic' | 'cartoon' | 'pixel' | 'fantasy';
    };
    ui: {
        titleImage?: string;
        leftDecoration?: string;
        rightDecoration?: string;
        buttonStyle: 'neon' | 'wood' | 'metal' | 'crystal' | 'stone';
        font: string;
    };
    audio?: {
        backgroundMusic?: string;
        spinSound?: string;
        winSound?: string;
        bigWinSound?: string;
    };
}

export const THEMES: Record<string, ThemeConfig> = {
    'retro80s': {
        id: 'retro80s',
        name: "Reel Heroes",
        description: "Neon-soaked Saturday morning cartoon nostalgia",
        colors: {
            background: [0x1a0033, 0x4d0080],
            reelFrame: 0x000000,
            reelBorder: 0xFF00FF,
            paylineColors: [0x00FFFF, 0xFF00FF, 0xFFFF00, 0x00FF00, 0xFF8000],
            uiAccent1: 0x00FFFF,
            uiAccent2: 0xFF00FF,
            uiAccent3: 0xFFFF00,
            winColor: 0xFFFF00,
            textColor: 0xFFFFFF
        },
        symbols: {
            path: '/assets/symbols/80s/',
            style: 'neon'
        },
        ui: {
            titleImage: '/assets/ui/80s/title_board.png',
            leftDecoration: '/assets/decorations/80s/left_decoration.png',
            rightDecoration: '/assets/decorations/80s/right_decoration.png',
            buttonStyle: 'neon',
            font: 'Orbitron'
        }
    },
    
    'ancientEgypt': {
        id: 'ancientEgypt',
        name: "Pharaoh's Fortune",
        description: "Journey through the pyramids for ancient treasures",
        colors: {
            background: [0x8B7355, 0x4A3C2A],
            reelFrame: 0x2F1F0F,
            reelBorder: 0xFFD700,
            paylineColors: [0xFFD700, 0xFF6B35, 0x8B4513, 0xDC143C, 0x4169E1],
            uiAccent1: 0xFFD700,
            uiAccent2: 0x8B4513,
            uiAccent3: 0xDC143C,
            winColor: 0xFFD700,
            textColor: 0xFFF8DC
        },
        symbols: {
            path: '/assets/symbols/egypt/',
            style: 'realistic'
        },
        ui: {
            titleImage: '/assets/ui/egypt/title_board.png',
            leftDecoration: '/assets/decorations/egypt/left_decoration.png',
            rightDecoration: '/assets/decorations/egypt/right_decoration.png',
            buttonStyle: 'stone',
            font: 'Papyrus'
        }
    },
    
    'underwaterAdventure': {
        id: 'underwaterAdventure',
        name: "Ocean's Treasures",
        description: "Dive deep for underwater riches",
        colors: {
            background: [0x001F3F, 0x003D7A],
            reelFrame: 0x001A33,
            reelBorder: 0x00CED1,
            paylineColors: [0x00CED1, 0x20B2AA, 0x48D1CC, 0x00FA9A, 0x7FFFD4],
            uiAccent1: 0x00CED1,
            uiAccent2: 0x20B2AA,
            uiAccent3: 0x00FA9A,
            winColor: 0x7FFFD4,
            textColor: 0xF0FFFF
        },
        symbols: {
            path: '/assets/symbols/ocean/',
            style: 'cartoon'
        },
        ui: {
            titleImage: '/assets/ui/ocean/title_board.png',
            leftDecoration: '/assets/decorations/ocean/left_decoration.png',
            rightDecoration: '/assets/decorations/ocean/right_decoration.png',
            buttonStyle: 'crystal',
            font: 'Arial'
        }
    },
    
    'spaceOdyssey': {
        id: 'spaceOdyssey',
        name: "Cosmic Slots",
        description: "Blast off to astronomical wins",
        colors: {
            background: [0x000033, 0x000066],
            reelFrame: 0x0F0F2F,
            reelBorder: 0x9400D3,
            paylineColors: [0x9400D3, 0x4B0082, 0x0000FF, 0x00FF00, 0xFFFF00],
            uiAccent1: 0x9400D3,
            uiAccent2: 0x00BFFF,
            uiAccent3: 0xFFFF00,
            winColor: 0xFFFFFF,
            textColor: 0xE0FFFF
        },
        symbols: {
            path: '/assets/symbols/space/',
            style: 'fantasy'
        },
        ui: {
            titleImage: '/assets/ui/space/title_board.png',
            leftDecoration: '/assets/decorations/space/left_decoration.png',
            rightDecoration: '/assets/decorations/space/right_decoration.png',
            buttonStyle: 'metal',
            font: 'Audiowide'
        }
    },
    
    'westernGold': {
        id: 'westernGold',
        name: "Wild West Gold Rush",
        description: "Strike it rich in the old frontier",
        colors: {
            background: [0x8B4513, 0xA0522D],
            reelFrame: 0x654321,
            reelBorder: 0xDAA520,
            paylineColors: [0xDAA520, 0xB8860B, 0xCD853F, 0xD2691E, 0xFF8C00],
            uiAccent1: 0xDAA520,
            uiAccent2: 0x8B4513,
            uiAccent3: 0xDC143C,
            winColor: 0xFFD700,
            textColor: 0xFFF8DC
        },
        symbols: {
            path: '/assets/symbols/western/',
            style: 'cartoon'
        },
        ui: {
            titleImage: '/assets/ui/western/title_board.png',
            leftDecoration: '/assets/decorations/western/left_decoration.png',
            rightDecoration: '/assets/decorations/western/right_decoration.png',
            buttonStyle: 'wood',
            font: 'Rye'
        }
    }
};

// Get current theme from localStorage or default to retro80s
export function getCurrentTheme(): ThemeConfig {
    const savedThemeId = localStorage.getItem('selectedTheme') || 'retro80s';
    return THEMES[savedThemeId] || THEMES['retro80s'];
}

// Save selected theme
export function setCurrentTheme(themeId: string): void {
    if (THEMES[themeId]) {
        localStorage.setItem('selectedTheme', themeId);
    }
}